import { AwaitContainer, Badge, DataGrid, type DataGridLeafColumn } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DownloadTemplateEntityType,
  type DownloadTemplateVariable,
  useDownloadTemplateVariables,
} from '@/entities/download-template';
import { dataGridHeightByRows } from '@/shared/lib';
import { DownloadTemplateAttributesHelp } from './DownloadTemplateAttributesHelp';

type Props = {
  /** Сущность открытой вкладки: показываются только её переменные */
  entityType: DownloadTemplateEntityType;
};

/**
 * Подсказка по сущности: какие переменные и атрибуты диалога система подставляет в шаблон, что
 * означает каждая и как их записывают в файле. Перечень ведёт сам код подстановки на бэкенде,
 * поэтому подсказка не расходится с тем, что подставляется.
 */
export const DownloadTemplateVariablesHelp: FC<Props> = ({ entityType }) => {
  const { t } = useTranslation('downloadTemplates');
  const { data, isLoading } = useDownloadTemplateVariables(entityType);

  const columns = useMemo<DataGridLeafColumn<DownloadTemplateVariable>[]>(
    () => [
      {
        id: 'name',
        header: t('variables.name'),
        size: 260,
        wrap: true,
        cell: ({ row }) => <code className="text-sm">{`{{ ${row.name} }}`}</code>,
      },
      {
        id: 'description',
        header: t('variables.description'),
        size: 560,
        wrap: true,
        cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <span>{row.description}</span>
            {row.isList && (
              <Badge variant="secondary" outline className="self-start">
                {t('isList')}
              </Badge>
            )}
            {row.fields?.length ? (
              <ul className="flex flex-col gap-0.5 text-sm text-muted-foreground">
                {row.fields.map((field) => (
                  <li key={field.name}>{`${row.name}.${field.name} — ${field.description}`}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ),
      },
    ],
    [t],
  );

  const variables = data?.variables ?? [];

  return (
    <AwaitContainer isLoading={isLoading}>
      <div className="flex flex-col gap-6">
        <DataGrid
          className="w-full"
          columns={columns}
          dataSource={{ mode: 'client', data: variables }}
          getRowId={(variable) => variable.name}
          height={dataGridHeightByRows(variables.length)}
          emptyState={t('variables.empty')}
          ariaLabel={t('variables.title')}
        />

        <div className="flex flex-col gap-3">
          <h3 className="font-medium">{t('variables.syntaxTitle')}</h3>
          {(data?.syntax ?? []).map((example) => (
            <div key={example.title} className="flex flex-col gap-1 rounded-lg border border-border p-3">
              <span className="font-medium">{example.title}</span>
              <code className="text-sm">{example.example}</code>
              <span className="text-sm text-muted-foreground">{example.description}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-3">
          <h3 className="font-medium">{t('attributes.title')}</h3>
          <DownloadTemplateAttributesHelp attributes={data?.attributes ?? []} />
        </div>
      </div>
    </AwaitContainer>
  );
};
